"use client"

import { useState, useId, type ReactNode } from "react"
import Image from "next/image"
import { Link } from "@local/ui/src/components/ui/Link"
import { Modal } from "@local/ui/src/components/ui/Modal"
import { Transition } from "@local/ui/src/Transition"
import { PlusIcon, MinusIcon } from "@local/ui/src/icons"
import type { FooterNavigationBadge, FooterNavigationItem } from "./types"
import dynamic from "next/dynamic"
import { getT } from "@local/i18n/src/t"
import {
  getLanguageForMarket,
  localizeMarketPath,
  marketBaseOf,
} from "@local/i18n/src/languages"
import { footer } from "@local/i18n/src/catalogs/footer"
import { a11y } from "@local/i18n/src/catalogs/a11y"

const BadgeImage = ({ badge }: { badge: FooterNavigationBadge }) => (
  <Image
    src={badge.src}
    alt={badge.alt}
    width={badge.width}
    height={badge.height}
    aria-hidden="true"
    className="h-4 w-auto"
  />
)

// Dynamically import static page components for modal display
const AboutUs = dynamic(
  () => import("../StaticPages/AboutUs").then((mod) => mod.AboutUs),
  { ssr: false }
)

const VeteransCharities = dynamic(
  () =>
    import("../StaticPages/VeteransCharities").then(
      (mod) => mod.VeteransCharities
    ),
  { ssr: false }
)

const BaerskinStories = dynamic(
  () =>
    import("../StaticPages/BaerskinStories").then((mod) => mod.BaerskinStories),
  { ssr: false }
)

const PrivacyPolicy = dynamic(
  () => import("../StaticPages/PrivacyPolicy").then((mod) => mod.PrivacyPolicy),
  { ssr: false }
)

const TermsConditions = dynamic(
  () =>
    import("../StaticPages/TermsConditions").then((mod) => mod.TermsConditions),
  { ssr: false }
)

const ShippingReturnsRefunds = dynamic(
  () =>
    import("../StaticPages/ShippingReturnsRefunds").then(
      (mod) => mod.ShippingReturnsRefunds
    ),
  { ssr: false }
)

const SecurePayments = dynamic(
  () =>
    import("../StaticPages/SecurePayments").then((mod) => mod.SecurePayments),
  { ssr: false }
)

const Accessibility = dynamic(
  () => import("../StaticPages/Accessibility").then((mod) => mod.Accessibility),
  { ssr: false }
)

/**
 * Determines if a URL should open in a modal instead of navigating
 */
const shouldOpenModal = (url: string) => {
  return (
    url.includes("about-us") ||
    url.includes("our-story") ||
    url.includes("privacy-policy") ||
    url.includes("terms-and-conditions") ||
    url.includes("shipping-returns-and-refunds") ||
    url.includes("secure-payments") ||
    url.includes("veterans-charities") ||
    url.includes("baerskin-stories") ||
    url.includes("accessibility")
  )
}

/**
 * Gets the appropriate modal content based on URL
 */
const getModalContent = (market: string, url?: string) => {
  if (!url) return null

  switch (true) {
    case url.includes("about-us") || url.includes("our-story"):
      return <AboutUs market={market} />
    case url.includes("privacy-policy"):
      return <PrivacyPolicy market={market} />
    case url.includes("terms-and-conditions"):
      return <TermsConditions market={market} />
    case url.includes("shipping-returns-and-refunds"):
      return <ShippingReturnsRefunds market={market} />
    case url.includes("secure-payments"):
      return <SecurePayments market={market} />
    case url.includes("veterans-charities"):
      return <VeteransCharities market={market} />
    case url.includes("baerskin-stories"):
      return <BaerskinStories market={market} />
    case url.includes("accessibility"):
      return <Accessibility market={market} />
    default:
      return null
  }
}

interface NavigationLinksCollapseProps {
  navigationFooter?: FooterNavigationItem[]
  market: string
  useModal?: boolean
}

export const NavigationLinksCollapse = ({
  navigationFooter,
  market,
  useModal = false,
}: NavigationLinksCollapseProps) => {
  return (
    <>
      {navigationFooter?.map((item, i) => (
        <CollapsibleSection
          key={`footer-collapse-${item.id}-${i}`}
          item={item}
          market={market}
          useModal={useModal}
        />
      ))}
    </>
  )
}

interface CollapsibleSectionProps {
  item: FooterNavigationItem
  market: string
  useModal?: boolean
}

interface FooterLinkProps {
  name: string
  url: string
  market: string
  isExternal: boolean
  badge?: FooterNavigationBadge
  useModal?: boolean
  onOpenModal?: (content: ReactNode) => void
}

const FooterLink = ({
  name,
  url,
  market,
  isExternal,
  badge,
  useModal = false,
  onOpenModal,
}: FooterLinkProps) => {
  const t = getT(getLanguageForMarket(market), { a11y })
  const openInModal = useModal && !isExternal && shouldOpenModal(url)
  const modalContent = getModalContent(market, url)

  if (isExternal) {
    return (
      <a
        href={url}
        target="_blank"
        rel="noreferrer"
        aria-label={t("a11y.openInNewTab", { name })}
        className="inline-flex items-center gap-2 text-base text-white hover:text-gray-300"
      >
        {badge && <BadgeImage badge={badge} />}
        <span>{name}</span>
      </a>
    )
  }

  if (openInModal) {
    return (
      <button
        onClick={() => onOpenModal?.(modalContent)}
        className="inline-flex items-center gap-2 text-left text-base text-white hover:text-gray-300"
        type="button"
        aria-label={t("a11y.openInModal", { name })}
      >
        {badge && <BadgeImage badge={badge} />}
        <span>{name}</span>
      </button>
    )
  }

  return (
    <Link
      href={localizeMarketPath(
        url.replace("%%MARKET%%", marketBaseOf(market)),
        market
      )}
      className="inline-flex items-center gap-2 text-base text-white hover:text-gray-300"
      prefetch={false}
    >
      {badge && <BadgeImage badge={badge} />}
      <span>{name}</span>
    </Link>
  )
}

const CollapsibleSection = ({
  item,
  market,
  useModal = false,
}: CollapsibleSectionProps) => {
  const [isOpen, setIsOpen] = useState(false)
  const [modalOpen, setModalOpen] = useState(false)
  const [modalContent, setModalContent] = useState<ReactNode>(null)
  const contentId = useId()
  const t = getT(getLanguageForMarket(market), { footer })

  const handleOpenModal = (content: ReactNode) => {
    setModalContent(content)
    setModalOpen(true)
  }

  const itemLabel = item.i18nKey
    ? t(item.i18nKey as Parameters<typeof t>[0])
    : item.name

  return (
    <>
      <div className="mb-4 border-b border-gray-600 pb-4">
        <button
          type="button"
          className="flex w-full flex-row items-center justify-between text-left text-white"
          onClick={() => setIsOpen(!isOpen)}
          aria-expanded={isOpen}
          aria-controls={contentId}
        >
          <h5 className="text-h5 leading-heading font-semibold">{itemLabel}</h5>
          <span className="text-white" aria-hidden="true">
            {isOpen ? <MinusIcon /> : <PlusIcon />}
          </span>
        </button>

        <Transition
          show={isOpen}
          enter="transition ease-out duration-200"
          enterFrom="opacity-0 scale-95"
          enterTo="opacity-100 scale-100"
          leave="transition ease-in duration-150"
          leaveFrom="opacity-100 scale-100"
          leaveTo="opacity-0 scale-95"
        >
          <div id={contentId} className="mt-4">
            <ul className="flex flex-col gap-3">
              {item.subitems
                ?.filter(
                  ({ markets }) =>
                    !markets || markets.includes(marketBaseOf(market))
                )
                .map(({ name, i18nKey, url, badge }, i) => {
                  const isExternal =
                    /^(https?|mailto|tel|ftp):/i.test(url) || !!item.external
                  const label = i18nKey
                    ? t(i18nKey as Parameters<typeof t>[0])
                    : name

                  return (
                    <li
                      key={`footer-collapse-subitem-${item.id}-${i}`}
                      className="whitespace-nowrap"
                    >
                      <FooterLink
                        name={label}
                        url={url}
                        market={market}
                        isExternal={isExternal}
                        badge={badge}
                        useModal={useModal}
                        onOpenModal={handleOpenModal}
                      />
                    </li>
                  )
                })}
            </ul>
          </div>
        </Transition>
      </div>
      {/* Modal rendered outside Transition to avoid transform positioning issues */}
      <Modal
        open={modalOpen}
        toggle={setModalOpen}
        contentClassName="max-w-4xl"
      >
        {modalContent}
      </Modal>
    </>
  )
}
